import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, ScrollView, Platform, Pressable } from 'react-native';
import { Text, TextInput } from '@constants/Themed';
import { YStack, XStack, H5, SizableText, Button, Spinner, Checkbox } from 'tamagui';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import apis from '@api/api';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useTranslation } from 'react-i18next';
import { getModernCardStyle, getModernTextColor } from '@stylesheet';
import { CHANGE_DATA, CHANGE_DATA_TITLE } from '@reduxLocal/actions/types';
import parseSchema from '@navigation/NavigationStacks/utils/parseSchema';
import * as Animatable from 'react-native-animatable';

export interface SchemaImportModalHandle {
  openModal: (connectionId: string, connectionName?: string) => void;
  closeModal: () => void;
}

interface SchemaImportModalProps {
  onImportComplete?: (schemas: any[]) => void;
}

const SchemaImportModal = React.forwardRef<SchemaImportModalHandle, SchemaImportModalProps>(
  ({ onImportComplete }, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = themeSelector();
    const [visible, setVisible] = useState(false);
    const [connectionId, setConnectionId] = useState<string | null>(null);
    const [connectionName, setConnectionName] = useState<string>('');
    const [tables, setTables] = useState<string[]>([]);
    const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [tags, setTags] = useState<string>('');

    React.useImperativeHandle(ref, () => ({
      openModal: (id: string, name?: string) => {
        setConnectionId(id);
        setConnectionName(name || '');
        setVisible(true);
        setSelectedTables(new Set());
        setTags('');
        loadTables(id);
      },
      closeModal: () => {
        setVisible(false);
        setConnectionId(null);
        setTables([]);
        setSelectedTables(new Set());
        setTags('');
      }
    }));

    const loadTables = useCallback(async (id: string) => {
      setLoading(true);
      try {
        const tableList = await apis.getDatabaseTables(id);
        setTables(tableList || []);
      } catch (error) {
        console.error('Error loading tables:', error);
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to load database tables', type: 'error' } });
      } finally {
        setLoading(false);
      }
    }, [dispatch]);

    const handleToggleTable = useCallback((tableName: string) => {
      setSelectedTables(prev => {
        const newSet = new Set(prev);
        if (newSet.has(tableName)) {
          newSet.delete(tableName);
        } else {
          newSet.add(tableName);
        }
        return newSet;
      });
    }, []);

    const handleSelectAll = useCallback(() => {
      if (selectedTables.size === tables.length) {
        setSelectedTables(new Set());
      } else {
        setSelectedTables(new Set(tables));
      }
    }, [tables, selectedTables.size]);

    const handleImport = useCallback(async () => {
      if (selectedTables.size === 0) {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please select at least one table to import', type: 'warning' } });
        return;
      }

      setImporting(true);
      dispatch({ type: SHOW_LOADING_MODAL });

      try {
        const tableNames = Array.from(selectedTables);
        const tagArray = tags.split(',').map(t => t.trim()).filter(t => t.length > 0);
        
        const importedSchemas = await apis.importDatabaseSchema(connectionId!, tableNames, tagArray);
        
        dispatch({ type: HIDE_LOADING_MODAL });

        if (importedSchemas && importedSchemas.length > 0) {
          // Use the first schema for navigation
          const firstSchema = importedSchemas[0];
          await parseSchema(firstSchema, dispatch);
          
          if (onImportComplete) {
            onImportComplete(importedSchemas);
          }
          
          setVisible(false);
        }
      } catch (error) {
        dispatch({ type: HIDE_LOADING_MODAL });
        console.error('Error importing schema:', error);
      } finally {
        setImporting(false);
      }
    }, [selectedTables, tags, connectionId, dispatch, onImportComplete]);

    if (!visible) return null;

    const cardStyle = getModernCardStyle(theme, 'large');

    return (
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
      >
        <Animatable.View
          animation="slideInUp"
          duration={300}
          style={[
            cardStyle,
            {
              width: Platform.OS === 'web' ? 600 : '90%',
              maxWidth: Platform.OS === 'web' ? 800 : '100%',
              maxHeight: Platform.OS === 'web' ? '80%' : '90%',
              padding: 20,
            }
          ]}
        >
          <YStack flex={1} gap="$3">
            {/* Header */}
            <XStack justifyContent="space-between" alignItems="center" marginBottom="$2">
              <H5
                color={getModernTextColor(theme, 'primary')}
                fontWeight="600"
              >
                Import Database Schema
              </H5>
              <Pressable onPress={() => setVisible(false)}>
                <MaterialCommunityIcons
                  name="close"
                  size={24}
                  color={getModernTextColor(theme, 'muted')}
                />
              </Pressable>
            </XStack>

            {connectionName && (
              <SizableText
                size="$3"
                color={getModernTextColor(theme, 'muted')}
                marginBottom="$2"
              >
                Connection: {connectionName}
              </SizableText>
            )}

            {/* Tags Input */}
            <YStack gap="$2">
              <SizableText
                size="$3"
                color={getModernTextColor(theme, 'secondary')}
                fontWeight="600"
              >
                Tags (comma-separated)
              </SizableText>
              <View
                style={{
                  backgroundColor: theme ? '#f8fafc' : '#1e293b',
                  borderRadius: 8,
                  padding: 12,
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#334155',
                }}
              >
                <TextInput
                  style={{
                    color: getModernTextColor(theme, 'primary'),
                    fontSize: 14,
                  }}
                  value={tags}
                  onChangeText={setTags}
                  placeholder="e.g. imported, database, production"
                />
              </View>
            </YStack>

            {/* Select All */}
            <XStack alignItems="center" gap="$2">
              <Checkbox
                checked={selectedTables.size === tables.length && tables.length > 0}
                onCheckedChange={handleSelectAll}
              >
                <Checkbox.Indicator>
                  <MaterialCommunityIcons name="check" size={16} color="white" />
                </Checkbox.Indicator>
              </Checkbox>
              <SizableText
                size="$3"
                color={getModernTextColor(theme, 'secondary')}
                onPress={handleSelectAll}
              >
                Select All ({selectedTables.size}/{tables.length})
              </SizableText>
            </XStack>

            {/* Tables List */}
            <ScrollView
              style={{
                flex: 1,
                maxHeight: Platform.OS === 'web' ? 400 : 300,
              }}
              showsVerticalScrollIndicator={true}
            >
              {loading ? (
                <YStack alignItems="center" justifyContent="center" padding="$4">
                  <Spinner size="large" color={theme ? '#3b82f6' : '#60a5fa'} />
                  <SizableText
                    size="$3"
                    color={getModernTextColor(theme, 'muted')}
                    marginTop="$2"
                  >
                    Loading tables...
                  </SizableText>
                </YStack>
              ) : tables.length === 0 ? (
                <YStack alignItems="center" justifyContent="center" padding="$4">
                  <MaterialCommunityIcons
                    name="database-off"
                    size={48}
                    color={getModernTextColor(theme, 'muted')}
                  />
                  <SizableText
                    size="$3"
                    color={getModernTextColor(theme, 'muted')}
                    marginTop="$2"
                    textAlign="center"
                  >
                    No tables found in this database
                  </SizableText>
                </YStack>
              ) : (
                <YStack gap="$2">
                  {tables.map((tableName) => (
                    <Pressable
                      key={tableName}
                      onPress={() => handleToggleTable(tableName)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 12,
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: selectedTables.has(tableName)
                          ? (theme ? '#3b82f6' : '#60a5fa')
                          : (theme ? '#e2e8f0' : '#334155'),
                      }}
                    >
                      <Checkbox
                        checked={selectedTables.has(tableName)}
                        onCheckedChange={() => handleToggleTable(tableName)}
                      >
                        <Checkbox.Indicator>
                          <MaterialCommunityIcons name="check" size={16} color="white" />
                        </Checkbox.Indicator>
                      </Checkbox>
                      <MaterialCommunityIcons
                        name="table"
                        size={20}
                        color={getModernTextColor(theme, 'secondary')}
                        style={{ marginLeft: 12 }}
                      />
                      <SizableText
                        size="$3"
                        color={getModernTextColor(theme, 'primary')}
                        marginLeft="$2"
                        flex={1}
                      >
                        {tableName}
                      </SizableText>
                    </Pressable>
                  ))}
                </YStack>
              )}
            </ScrollView>

            {/* Actions */}
            <XStack gap="$2" marginTop="$3">
              <Button
                flex={1}
                onPress={() => setVisible(false)}
                backgroundColor={theme ? '#6b7280' : '#4b5563'}
                color="white"
              >
                <SizableText color="white" size="$3">
                  Cancel
                </SizableText>
              </Button>
              <Button
                flex={1}
                onPress={handleImport}
                disabled={importing || selectedTables.size === 0}
                backgroundColor={theme ? '#10b981' : '#059669'}
                color="white"
                opacity={selectedTables.size === 0 ? 0.5 : 1}
              >
                {importing ? (
                  <Spinner size="small" color="white" />
                ) : (
                  <SizableText color="white" size="$3">
                    Import ({selectedTables.size})
                  </SizableText>
                )}
              </Button>
            </XStack>
          </YStack>
        </Animatable.View>
      </View>
    );
  }
);

SchemaImportModal.displayName = 'SchemaImportModal';

export default SchemaImportModal;

